
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toPng } from "html-to-image";

import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import MobileBottomNav from '../../components/MobileBottomNav';


const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = 'تأكيد',
  cancelLabel = 'إلغاء',
  onConfirm,
  onCancel,
  loading = false,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center px-4" dir="rtl">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={loading ? undefined : onCancel}
      />
      <div className="relative w-full max-w-md border border-[rgba(40,227,218,.22)] bg-[rgba(9,28,43,.97)] p-6 space-y-4 shadow-2xl" style={{ borderRadius: '20px', backdropFilter: 'blur(6px)' }}>
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-[18px] font-bold text-white">{title}</h2>
          <button
            onClick={onCancel}
            disabled={loading}
            className="text-[#B8C5CE] hover:text-white text-sm disabled:opacity-50"
          >
            إغلاق
          </button>
        </div>
        <p className="text-[14px] text-[#C4D6E5] leading-relaxed">{message}</p>
        <div className="flex justify-end gap-3 mt-2">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 rounded-[12px] text-[14px] font-medium border border-[#2C4258] text-[#D0E3F0] hover:bg-[#111D2A] disabled:opacity-60"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 rounded-[12px] text-[14px] font-bold text-[#081423] bg-gradient-to-tr from-[#F6414F] to-[#FF9B63] shadow-[0_0_16px_rgba(246,65,79,.55)] hover:brightness-110 disabled:opacity-60 disabled:shadow-none"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};


const makeSlug = (val) => {
  if (!val) return '';
  const clean = String(val).trim().replace(/\s+/g, '-');
  return encodeURIComponent(clean);
};

const resolveKind = (review) => {
  if (!review) return 'doctor';
  if (review.course || review.courseSlug) return 'course';
  if (review.doctorSlug || review.doctorName || review.doctor || review.lecturer)
    return 'doctor';
  return 'doctor';
};


const DAY_OPTIONS = [
  { key: 'U', label: 'الأحد' },
  { key: 'M', label: 'الإثنين' },
  { key: 'T', label: 'الثلاثاء' },
  { key: 'W', label: 'الأربعاء' },
  { key: 'R', label: 'الخميس' },
];

const TIME_MAP = new Map([
  ['08:00', '10:00'], ['09:00', '10:40'], ['09:30', '11:00'],
  ['10:00', '11:20'], ['10:30', '11:40'], ['11:00', '13:00'],
  ['12:00', '13:40'], ['13:00', '14:20'], ['14:00', '15:00'],
  ['14:30', '15:20'], ['15:00', '15:40'], ['16:00', '16:20'],
  ['17:00', '21:20'], ['17:30', '21:40'], ['18:00', '22:00'],
  ['18:30', '22:20'], ['19:00', '22:40'], ['19:30', '23:00'],
  ['20:00', '23:20'], ['21:00', '00:00'], ['22:00', '00:40'],
  ['23:00', '01:20'],
]);

const DURATION_MAP = { '50': '35', '75': '55', '100': '70' };

const pad2 = (n) => String(n).padStart(2, '0');

const isValidHHMM = (s) => {
  if (typeof s !== 'string') return false;
  const m = s.match(/^(\d{2}):(\d{2})$/);
  if (!m) return false;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  return hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59;
};

const formatTime12 = (hhmm) => {
  if (!isValidHHMM(hhmm)) return '—';
  const [hStr, mStr] = hhmm.split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad2(m)} ${suffix}`;
};

const normalizeDays = (days) => {
  const set = new Set();
  const arr = Array.isArray(days) ? days : [];
  for (const d of arr) {
    const k = String(d || '').trim().toUpperCase();
    if (DAY_OPTIONS.some((x) => x.key === k)) set.add(k);
  }
  return Array.from(set);
};

const daysAbbrevFromKeys = (keys) => {
  const arr = normalizeDays(keys);
  if (!arr.length) return '—';
  return arr.join('');
};

const daysLabelFromKeys = (keys) => {
  const arr = normalizeDays(keys);
  if (!arr.length) return '—';
  const map = new Map(DAY_OPTIONS.map((d) => [d.key, d.label]));
  return arr.map((k) => map.get(k) || k).join('، ');
};

const isRemoteLikely = (ramadanTime) => {
  if (!isValidHHMM(ramadanTime)) return false;
  const [h, m] = ramadanTime.split(':').map(Number);
  const mins = h * 60 + m;
  const t2100 = 21 * 60;
  const t0300 = 3 * 60;
  return mins >= t2100 || mins < t0300;
};

const RamadanBadge = ({ ok, remote }) => {
  const badge = ok
    ? remote
      ? { text: 'عن بُعد', cls: 'bg-orange-500/20 text-orange-400' }
      : { text: 'حضوري', cls: 'bg-green-500/20 text-green-400' }
    : { text: 'غير مدعوم', cls: 'bg-red-500/20 text-red-400' };

  return <span className={`px-2 py-1 rounded text-[10px] font-bold ${badge.cls}`}>{badge.text}</span>;
};


const UserReviewsModal = ({
  open,
  user,
  reviews,
  loading,
  error,
  onClose,
  onDelete,
  onOpenReviewPage,
  onDeleteAll,
  deletingAll = false,
}) => {
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('all');

  useEffect(() => {
    if (!open) return;
    setQ('');
    setKind('all');
  }, [open]);

  const summary = useMemo(() => {
    const total = Array.isArray(reviews) ? reviews.length : 0;
    const withComment = Array.isArray(reviews)
      ? reviews.filter((r) => !!(r && r.comment && String(r.comment).trim())).length
      : 0;
    const doctors = Array.isArray(reviews)
      ? reviews.filter((r) => !!(r && (r.doctorName || r.doctorSlug || r.doctor || r.lecturer))).length
      : 0;
    const courses = Array.isArray(reviews)
      ? reviews.filter((r) => !!(r && (r.course || r.courseSlug))).length
      : 0;
    return { total, withComment, doctors, courses };
  }, [reviews]);

  const filtered = useMemo(() => {
    const list = Array.isArray(reviews) ? reviews : [];
    const qq = q.trim().toLowerCase();

    return list
      .filter((r) => {
        if (!r) return false;
        if (kind === 'doctor') return !!(r.doctorName || r.doctorSlug || r.doctor || r.lecturer);
        if (kind === 'course') return !!(r.course || r.courseSlug);
        if (kind === 'comment') return !!(r.comment && String(r.comment).trim());
        return true;
      })
      .filter((r) => {
        if (!qq) return true;
        const hay = [
          r.doctorName,
          r.doctorSlug,
          r.doctor,
          r.lecturer,
          r.course,
          r.courseSlug,
          r.comment,
          r.grade,
          r.classification,
          r.timestamp,
          r.id,
        ]
          .filter(Boolean)
          .join(' | ')
          .toLowerCase();
        return hay.includes(qq);
      });
  }, [reviews, q, kind]);

  if (!open || !user) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center px-4" dir="rtl">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl max-h-[82vh] overflow-hidden border border-[rgba(40,227,218,.22)] bg-[rgba(9,28,43,.97)] shadow-2xl flex flex-col" style={{ borderRadius: '20px', backdropFilter: 'blur(6px)' }}>
        <div className="px-5 py-4 border-b border-[#1A3245] flex items-center justify-between gap-3">
          <div className="flex flex-col">
            <span className="text-[15px] font-bold text-white">تقييمات المستخدم</span>
            <span className="text-[12px] text-[#9EB4C8] ltr">{user.email}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onDeleteAll}
              disabled={loading || deletingAll || (Array.isArray(reviews) ? reviews.length === 0 : true)}
              className="px-3 py-2 rounded-[12px] text-[12px] font-bold text-[#FFECEC] bg-[#2B1014] border border-[#F76A7A99] hover:bg-[#3A151A] disabled:opacity-60"
              title="حذف جميع تقييمات هذا المستخدم"
            >
              {deletingAll ? 'جاري الحذف…' : 'حذف الكل'}
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0B1723] border border-[#2C4258] text-[#C4D6E5] hover:text-white hover:bg-[#122133]"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="px-5 py-4 border-b border-[rgba(40,227,218,.15)] bg-[rgba(8,20,35,.6)]">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <div className="rounded-[14px] border border-[rgba(40,227,218,.22)] bg-[rgba(8,20,35,.8)] px-3 py-2">
              <div className="text-[11px] text-[#9EB4C8]">الإجمالي</div>
              <div className="text-[14px] font-bold text-white">{summary.total}</div>
            </div>
            <div className="rounded-[14px] border border-[rgba(40,227,218,.22)] bg-[rgba(8,20,35,.8)] px-3 py-2">
              <div className="text-[11px] text-[#9EB4C8]">بتعليق</div>
              <div className="text-[14px] font-bold text-white">{summary.withComment}</div>
            </div>
            <div className="rounded-[14px] border border-[rgba(40,227,218,.22)] bg-[rgba(8,20,35,.8)] px-3 py-2">
              <div className="text-[11px] text-[#9EB4C8]">مرتبطة بمحاضر</div>
              <div className="text-[14px] font-bold text-white">{summary.doctors}</div>
            </div>
            <div className="rounded-[14px] border border-[rgba(40,227,218,.22)] bg-[rgba(8,20,35,.8)] px-3 py-2">
              <div className="text-[11px] text-[#9EB4C8]">مرتبطة بمقرر</div>
              <div className="text-[14px] font-bold text-white">{summary.courses}</div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
            <div className="flex-1">
              <div
                className="flex items-center gap-2 rounded-[14px] px-3 py-2"
                style={{
                  border: '1px solid rgba(40,227,218,.25)',
                  background:
                    'radial-gradient(45% 100% at 50% -20%, rgba(40,227,218,.22) 0%, rgba(40,227,218,0) 45%), rgba(6,18,32,.85)',
                }}
              >
                <span className="text-[#B8C5CE] text-[12px]">بحث</span>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="ابحث داخل تقييمات المستخدم…"
                  className="flex-1 bg-transparent outline-none text-[13px] text-white placeholder:text-[#7C96AA] text-end"
                />
                {q && (
                  <button
                    onClick={() => setQ('')}
                    className="text-[#B8C5CE] hover:text-white text-[12px]"
                    title="مسح"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              {[
                { k: 'all', t: 'الكل' },
                { k: 'doctor', t: 'محاضر' },
                { k: 'course', t: 'مقرر' },
                { k: 'comment', t: 'بتعليق' },
              ].map((x) => {
                const active = kind === x.k;
                return (
                  <button
                    key={x.k}
                    onClick={() => setKind(x.k)}
                    className="px-3 py-2 rounded-[14px] text-[12px] font-bold transition-all"
                    style={{
                      color: active ? '#081423' : '#D0E3F0',
                      background: active
                        ? 'linear-gradient(135deg,#28E3DA,#7DF9E8)'
                        : 'rgba(7,22,36,0.65)',
                      border: active ? '1px solid rgba(8,20,35,.6)' : '1px solid rgba(40,227,218,.18)',
                      boxShadow: active ? '0 0 18px rgba(40,227,218,.40)' : 'none',
                    }}
                  >
                    {x.t}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between text-[12px] text-[#9EB4C8]">
            <div>نتائج: <span className="text-white font-bold">{filtered.length}</span></div>
            <div className="opacity-80">اضغط “صفحة التقييم” لفتح التقييم في الصفحة الأصلية</div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 custom-scrollbar">
          {loading && (
            <div className="text-center text-[#B8C5CE] text-[14px] py-6">جاري تحميل التقييمات…</div>
          )}

          {!loading && error && (
            <div className="text-center text-[13px] text-red-400 py-4">{error}</div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="text-center text-[#B8C5CE] text-[14px] py-6">
              لا يوجد نتائج مطابقة.
            </div>
          )}

          {!loading &&
            !error &&
            filtered.map((review) => (
              <div
                key={review.id}
                className="px-4 py-3"
                style={{ background: 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.10) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.9)', border: '1px solid rgba(40,227,218,.22)', borderRadius: '18px', boxShadow: '0 4px 20px rgba(0,0,0,.3)', backdropFilter: 'blur(6px)' }}
              >
                <div className="flex justify-between items-center gap-4 mb-2">
                  <div className="text-[13px] text-[#DDEAF6] font-medium">
                    {review.doctorName
                      ? `المحاضر: ${review.doctorName}`
                      : review.course
                      ? `المقرر: ${review.course}`
                      : 'بدون ارتباط محدد'}
                  </div>
                  <div className="text-[11px] text-[#7DE9FF]">{review.timestamp || ''}</div>
                </div>

                {review.course && review.doctorName && (
                  <div className="flex justify-between items-center text-[12px] text-[#9EB4C8] mb-1">
                    <span className="ltr">{review.course}</span>
                    <span>المقرر</span>
                  </div>
                )}

                {review.comment && (
                  <div className="mt-2 mb-2">
                    <div className="text-[12px] text-[#9EB4C8] mb-1">نص التقييم:</div>
                    <div className="text-[13px] text-[#E4F0FF] leading-relaxed whitespace-pre-wrap">
                      {review.comment}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mt-2">
                  {Array.isArray(review.tags) &&
                    review.tags.map((t, idx) => {
                      const txt =
                        typeof t === 'string'
                          ? t
                          : t?.text || t?.label || t?.value || '';
                      if (!txt) return null;
                      return (
                        <span
                          key={`${review.id}-tag-${idx}`}
                          className="px-2 py-1 rounded-full text-[11px] text-[#D0F7FF] bg-[#0B2A30] border border-[#28E3DA55]"
                        >
                          {txt}
                        </span>
                      );
                    })}
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
                  <div className="text-[12px] text-[#B8C5CE]">
                    الدرجة النهائية:{' '}
                    <span className="text-[#28E3DA] font-semibold">
                      {review.grade || review.classification || 'غير محدد'}
                    </span>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => onOpenReviewPage(review)}
                      className="px-3 py-1.5 rounded-[12px] text-[12px] font-semibold text-[#081423] bg-gradient-to-tr from-[#28E3DA] to-[#7DF9E8] shadow-[0_0_14px_rgba(40,227,218,.55)] hover:brightness-110"
                    >
                      صفحة التقييم
                    </button>
                    <button
                      onClick={() => onDelete(review)}
                      className="px-3 py-1.5 rounded-[12px] text-[12px] font-semibold text-[#FFECEC] bg-[#2B1014] border border-[#F76A7A99] hover:bg-[#3A151A]"
                    >
                      حذف التقييم
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>

        <div className="px-5 py-4 border-t border-[#10273A] flex items-center justify-between gap-3">
          <div className="text-[12px] text-[#9EB4C8]">
            {filtered.length > 0 ? 'تلميح: استخدم البحث لتصفية التقييمات بسرعة.' : ''}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-[14px] text-[13px] font-bold text-[#D0E3F0] border border-[#2C4258] hover:bg-[#111D2A]"
          >
            إغلاق
          </button>
        </div>
      </div>
    </div>
  );
};


const UserRamadanModal = ({
  open,
  user,
  data,
  loading,
  error,
  onClose,
  onRequestDeleteSchedule,
  onDeleteRow,
}) => {
  const tableRef = useRef(null);

  const rows = useMemo(() => {
    const arr = Array.isArray(data?.schedule) ? data.schedule : [];
    return arr.map((r) => {
      const before = typeof r?.before === "string" ? r.before : "";
      const durBefore = String(r?.durBefore ?? "50");
      const name = String(r?.name || "بدون اسم");
      const days = normalizeDays(r?.days);
      const ramadan = TIME_MAP.get(before) || "";
      const ok = !!ramadan;
      const durAfter = ok ? DURATION_MAP[durBefore] || "—" : "—";
      const remote = ok ? isRemoteLikely(ramadan) : false;

      return {
        id: r?.id || `${name}_${before}_${days.join("")}`,
        name,
        before,
        days,
        daysAbbrev: daysAbbrevFromKeys(days),
        daysLabel: daysLabelFromKeys(days),
        ramadan,
        ok,
        remote,
        durBefore,
        durAfter,
      };
    });
  }, [data]);

const waitForImages = async (root) => {
  const imgs = Array.from(root.querySelectorAll("img"));
  await Promise.all(
    imgs.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalWidth > 0) return resolve();
          const done = () => resolve();
          img.addEventListener("load", done, { once: true });
          img.addEventListener("error", done, { once: true });
        })
    )
  );
};

const exportImage = async () => {
  const root = tableRef.current;
  if (!root) return;

  try {
    const clone = root.cloneNode(true);

    const host = document.createElement("div");
    host.style.position = "fixed";
    host.style.inset = "0";
    host.style.zIndex = "999999";
    host.style.pointerEvents = "none";
    host.style.opacity = "0";
    host.style.background = "transparent";

    clone.style.maxWidth = "none";
    clone.style.width = "auto";
    clone.style.overflow = "visible";
    clone.style.direction = "rtl";
    clone.style.color = "#ffffff";
    clone.style.background = "#081423";

    host.appendChild(clone);
    document.body.appendChild(host);

    await new Promise((r) => requestAnimationFrame(r));
    if (document.fonts?.ready) await document.fonts.ready;
    await waitForImages(clone);
    await new Promise((r) => requestAnimationFrame(r));

    const fullW = Math.ceil(
      Math.max(clone.scrollWidth, clone.getBoundingClientRect().width)
    );
    const fullH = Math.ceil(
      Math.max(clone.scrollHeight, clone.getBoundingClientRect().height)
    );

    clone.style.width = `${fullW}px`;

    await new Promise((r) => requestAnimationFrame(r));

    const dataUrl = await toPng(clone, {
      cacheBust: true,
      pixelRatio: fullW >= 1400 ? 1.5 : 2,
      backgroundColor: "#081423",
      width: fullW,
      height: fullH,
      style: {
        width: `${fullW}px`,
        height: `${fullH}px`,
        direction: "rtl",
        color: "#ffffff",
        background: "#081423",
      },
    });

    host.remove();

    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `ramadan_schedule_${user.email}.png`;
    link.click();
  } catch {
    alert("فشل تصدير الصورة");
  }
};


  if (!open || !user) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center px-4" dir="rtl">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-6xl max-h-[90vh] overflow-hidden border border-[rgba(40,227,218,.22)] bg-[rgba(9,28,43,.97)] shadow-2xl flex flex-col" style={{ borderRadius: '20px', backdropFilter: 'blur(6px)' }}>
        <div className="px-5 py-4 border-b border-[#1A3245] flex items-center justify-between gap-3">
          <div>
            <div className="text-[15px] font-bold text-white">جدول رمضان للمستخدم</div>
            <div className="text-[12px] text-[#9EB4C8] ltr">{user.email}</div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={exportImage}
              className="px-3 py-2 rounded-[12px] text-[12px] font-bold text-[#081423] bg-gradient-to-tr from-[#28E3DA] to-[#7DF9E8]"
            >
              حفظ كصورة
            </button>

            <button
              onClick={onRequestDeleteSchedule}
              className="px-3 py-2 rounded-[12px] text-[12px] font-bold text-[#FFECEC] bg-[#2B1014] border border-[#F76A7A99]"
            >
              حذف الجدول كامل
            </button>

            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0B1723] border border-[#2C4258] text-[#C4D6E5]"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && <div className="text-center text-[#B8C5CE] py-10">جاري التحميل…</div>}
          {!loading && error && <div className="text-center text-red-400 py-6">{error}</div>}

          {!loading && !error && rows.length === 0 && (
            <div className="text-center text-[#B8C5CE] py-10">لا يوجد جدول محفوظ.</div>
          )}

          {!loading && !error && rows.length > 0 && (
            <div ref={tableRef} className="overflow-x-auto">
              <table className="w-full border-separate [border-spacing:0_8px] text-right">
                <thead>
                  <tr className="text-[10px] text-white/40">
                    <th className="px-4">المادة</th>
                    <th className="px-4">الأيام</th>
                    <th className="px-4">الوقت المعتاد</th>
                    <th className="px-4 text-[#28e3da]">وقت رمضان</th>
                    <th className="px-4">المدة الجديدة</th>
                    <th className="px-4">الحالة</th>
                    <th className="px-4">إجراء</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="bg-white/5">
                      <td className="px-4 py-4 font-bold">{r.name}</td>
                      <td className="px-4 py-4">{r.daysAbbrev}</td>
                      <td className="px-4 py-4">{formatTime12(r.before)}</td>
                      <td className="px-4 py-4 font-black text-[#28e3da]">
                        {r.ok ? formatTime12(r.ramadan) : "—"}
                      </td>
                      <td className="px-4 py-4">
                        {r.durBefore} ⮕ {r.ok ? r.durAfter : "—"} د
                      </td>
                      <td className="px-4 py-4">
                        <RamadanBadge ok={r.ok} remote={r.remote} />
                      </td>
                      <td className="px-4 py-4">
                        <button
                          onClick={() => onDeleteRow(r.id)}
                          className="px-3 py-1 rounded-[10px] text-[11px] font-bold text-[#FFECEC] bg-[#2B1014] border border-[#F76A7A99]"
                        >
                          حذف المادة
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-[#10273A] flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-[14px] text-[13px] font-bold text-[#D0E3F0] border border-[#2C4258]"
          >
            إغلاق
          </button>
        </div>
      </div>
    </div>
  );
};



const NOTIF_TYPES = [
  { value: 'general',  label: 'عام' },
  { value: 'system',   label: 'نظام' },
  { value: 'security', label: 'أمان' },
  { value: 'social',   label: 'تفاعل' },
];

function getCsrfCookie() {
  const m = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);
  return m ? decodeURIComponent(m[1]) : '';
}

const SendNotificationModal = ({ open, users, onClose, onSuccess }) => {
  const [title, setTitle]       = useState('');
  const [body, setBody]         = useState('');
  const [type, setType]         = useState('general');
  const [mode, setMode]         = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [excludeIds, setExcludeIds]   = useState([]);
  const [sending, setSending]   = useState(false);
  const [error, setError]       = useState('');

  const allUsersList = useMemo(() => {
    const list = Array.isArray(users) ? users : [];
    const seen = new Set();
    return list.filter((u) => {
      const key = String(u.id ?? u.email ?? '');
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [users]);

  const toggleId = (id, setter) =>
    setter(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSend = async () => {
    setError('');
    if (!title.trim()) { setError('العنوان مطلوب'); return; }
    if (mode === 'selected' && selectedIds.length === 0) { setError('اختر مستخدماً على الأقل'); return; }
    if (mode === 'role'     && selectedRoles.length === 0) { setError('اختر صلاحية على الأقل'); return; }

    const payload = {
      title: title.trim(),
      body: body.trim(),
      type,
      all: mode === 'all',
      userIds: mode === 'selected' ? selectedIds : [],
      roles:   mode === 'role'     ? selectedRoles : [],
      excludeIds,
    };

    setSending(true);
    try {
      const csrf = getCsrfCookie();
      const res = await fetch('/api/admin/notifications/send', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrf ? { 'X-XSRF-TOKEN': csrf } : {}),
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || data.error || 'فشل إرسال الإشعار');
        return;
      }
      onSuccess && onSuccess(data.sent || 0);
      onClose();

      setTitle(''); setBody(''); setType('general'); setMode('all');
      setSelectedIds([]); setSelectedRoles([]); setExcludeIds([]);
    } catch {
      setError('تعذر الاتصال بالخادم');
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center px-4" dir="rtl">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={sending ? undefined : onClose} />
      <div
        className="relative w-full max-w-lg border border-[rgba(40,227,218,.22)] bg-[rgba(9,28,43,.97)] p-6 space-y-4 shadow-2xl overflow-y-auto"
        style={{ maxHeight: '90vh', borderRadius: '20px', backdropFilter: 'blur(6px)' }}
      >
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-[17px] font-bold text-white">إرسال إشعار</h2>
          <button onClick={onClose} disabled={sending} className="text-[#B8C5CE] hover:text-white text-sm disabled:opacity-50">إغلاق</button>
        </div>

        {           }
        <div className="space-y-1">
          <label className="text-[12px] text-[#9EB4C8]">العنوان *</label>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={200}
            placeholder="عنوان الإشعار"
            className="w-full bg-[rgba(8,20,35,.8)] border border-[rgba(40,227,218,.22)] rounded-[12px] px-3 py-2 text-[13px] text-white outline-none focus:border-[#28E3DA70]"
          />
        </div>

        {          }
        <div className="space-y-1">
          <label className="text-[12px] text-[#9EB4C8]">النص (اختياري)</label>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            maxLength={1000}
            rows={3}
            placeholder="تفاصيل الإشعار..."
            className="w-full bg-[rgba(8,20,35,.8)] border border-[rgba(40,227,218,.22)] rounded-[12px] px-3 py-2 text-[13px] text-white outline-none focus:border-[#28E3DA70] resize-none"
          />
        </div>

        {          }
        <div className="space-y-1">
          <label className="text-[12px] text-[#9EB4C8]">النوع</label>
          <div className="flex flex-wrap gap-2">
            {NOTIF_TYPES.map(t => (
              <button
                key={t.value}
                onClick={() => setType(t.value)}
                className="px-3 py-1 rounded-full text-[12px] font-medium border transition-all"
                style={{
                  background: type === t.value ? 'rgba(40,227,218,0.2)' : 'transparent',
                  borderColor: type === t.value ? 'rgba(40,227,218,0.7)' : 'rgba(40,227,218,0.2)',
                  color: '#fff',
                }}
              >{t.label}</button>
            ))}
          </div>
        </div>

        {                 }
        <div className="space-y-1">
          <label className="text-[12px] text-[#9EB4C8]">المستهدفون</label>
          <div className="flex gap-2">
            {[{v:'all',l:'الكل'},{v:'selected',l:'مستخدمون محددون'},{v:'role',l:'حسب الصلاحية'}].map(m => (
              <button
                key={m.v}
                onClick={() => setMode(m.v)}
                className="px-3 py-1 rounded-full text-[12px] font-medium border transition-all"
                style={{
                  background: mode === m.v ? 'rgba(40,227,218,0.2)' : 'transparent',
                  borderColor: mode === m.v ? 'rgba(40,227,218,0.7)' : 'rgba(40,227,218,0.2)',
                  color: '#fff',
                }}
              >{m.l}</button>
            ))}
          </div>
        </div>

        {                    }
        {mode === 'selected' && (
          <div className="space-y-1">
            <label className="text-[12px] text-[#9EB4C8]">اختر المستخدمين</label>
            <div className="max-h-40 overflow-y-auto space-y-1 border border-[#28E3DA20] rounded-xl p-2">
              {allUsersList.length === 0 && <p className="text-[12px] text-[#9EB4C8] text-center py-2">لا يوجد مستخدمون</p>}
              {allUsersList.map(u => (
                <label key={`sel-${u.id}`} className="flex items-center gap-2 cursor-pointer hover:bg-white/5 rounded px-2 py-1">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(u.id)}
                    onChange={() => toggleId(u.id, setSelectedIds)}
                    className="accent-[#28E3DA]"
                  />
                  <span className="text-[12px] text-white">{u.username || u.email}</span>
                  <span className="text-[11px] text-[#9EB4C8]">{u.role}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {                   }
        {mode === 'role' && (
          <div className="space-y-1">
            <label className="text-[12px] text-[#9EB4C8]">اختر الصلاحيات</label>
            <div className="flex flex-wrap gap-2">
              {['user','admin','developer','owner'].map(r => (
                <label key={r} className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedRoles.includes(r)}
                    onChange={() => toggleId(r, setSelectedRoles)}
                    className="accent-[#28E3DA]"
                  />
                  <span className="text-[12px] text-white">{r}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {                                        }
        {mode !== 'selected' && allUsersList.length > 0 && (
          <div className="space-y-1">
            <label className="text-[12px] text-[#9EB4C8]">استثناء مستخدمين (اختياري)</label>
            <div className="max-h-28 overflow-y-auto space-y-1 border border-[#28E3DA20] rounded-xl p-2">
              {allUsersList.map(u => (
                <label key={`exc-${u.id}`} className="flex items-center gap-2 cursor-pointer hover:bg-white/5 rounded px-2 py-1">
                  <input
                    type="checkbox"
                    checked={excludeIds.includes(u.id)}
                    onChange={() => toggleId(u.id, setExcludeIds)}
                    className="accent-red-400"
                  />
                  <span className="text-[12px] text-white">{u.username || u.email}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {error && <p className="text-[12px] text-red-400">{error}</p>}

        <div className="flex justify-end gap-3 pt-1">
          <button
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 rounded-[12px] text-[14px] font-medium border border-[#2C4258] text-[#D0E3F0] hover:bg-[#111D2A] disabled:opacity-60"
          >إلغاء</button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="px-4 py-2 rounded-[12px] text-[14px] font-bold text-[#081423] bg-gradient-to-tr from-[#28E3DA] to-[#7DF9E8] shadow-[0_0_16px_rgba(40,227,218,.45)] hover:brightness-110 disabled:opacity-60 disabled:shadow-none"
          >{sending ? 'جاري الإرسال…' : 'إرسال الإشعار'}</button>
        </div>
      </div>
    </div>
  );
};


const UserManagementPage = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const [sortOrder, setSortOrder] = useState('desc');
  const [sortKey, setSortKey] = useState('registeredSince');
  const [activityFilter, setActivityFilter] = useState('all');

  const [selectedUser, setSelectedUser] = useState(null);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [userReviews, setUserReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewsError, setReviewsError] = useState('');


  const [showRamadanModal, setShowRamadanModal] = useState(false);
  const [ramadanLoading, setRamadanLoading] = useState(false);
  const [ramadanError, setRamadanError] = useState('');
  const [ramadanData, setRamadanData] = useState({ schedule: [], updatedAt: null });

  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [deletingReview, setDeletingReview] = useState(false);

  const [userToDelete, setUserToDelete] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);

  const [deletingAllReviews, setDeletingAllReviews] = useState(false);


  const [confirmDeleteScheduleOpen, setConfirmDeleteScheduleOpen] = useState(false);
  const [deletingSchedule, setDeletingSchedule] = useState(false);


  const [showSendNotifModal, setShowSendNotifModal] = useState(false);

  const [banner, setBanner] = useState({ type: '', text: '' });
  const bannerTimer = useRef(null);

  const [ramadanSchedulesCount, setRamadanSchedulesCount] = useState(0);
  const [loadingRamadanSchedulesCount, setLoadingRamadanSchedulesCount] = useState(false);

  const showBanner = (type, text) => {
    setBanner({ type, text });
    if (bannerTimer.current) clearTimeout(bannerTimer.current);
    bannerTimer.current = setTimeout(() => setBanner({ type: '', text: '' }), 3500);
  };
const deleteRamadanRow = async (rowId) => {
  if (!selectedUser?.email) return;

  try {
    const res = await fetch("/api/admin/ramadan/schedules/row", {
      method: "DELETE",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: selectedUser.email,
        rowId,
      }),
    });

    if (!res.ok) throw new Error("failed_delete_row");
    const payload = await res.json().catch(() => ({}));
    if (!payload?.ok) throw new Error(payload?.error || "failed_delete_row");

    setRamadanData((prev) => ({
      ...prev,
      schedule: prev.schedule.filter((x) => x.id !== rowId),
    }));

    showBanner("ok", "تم حذف المادة.");
  } catch {
    showBanner("err", "تعذر حذف المادة.");
  }
};

  const toValidDate = (val) => {
    if (!val && val !== 0) return null;
    if (val instanceof Date) return isNaN(val) ? null : val;
    if (typeof val === 'number') {
      const dNum = new Date(val);
      return isNaN(dNum) ? null : dNum;
    }
    if (typeof val === 'string') {
      const try1 = new Date(val);
      if (!isNaN(try1)) return try1;
      const try2 = new Date(val + 'Z');
      if (!isNaN(try2)) return try2;
    }
    return null;
  };

  function timeAgoClient(iso, opts = {}) {
    const { isModified = false, now: nowOverride } = opts;
    try {
      if (!iso) return '';
      const then = toValidDate(iso);
      if (!then) return '';
      const now = nowOverride instanceof Date ? nowOverride : new Date();
      const diffMs = now - then;
      if (!isFinite(diffMs)) return '';
      const differenceInMinutes = Math.floor(diffMs / 60000);
      if (differenceInMinutes < 0) return '';
      let timeText = '';
      if (differenceInMinutes < 1) {
        timeText = 'الآن';
      } else if (differenceInMinutes < 60) {
        if (differenceInMinutes === 1) timeText = 'قبل دقيقة';
        else if (differenceInMinutes === 2) timeText = 'قبل دقيقتين';
        else if (differenceInMinutes <= 10) timeText = `قبل ${differenceInMinutes} دقايق`;
        else timeText = `قبل ${differenceInMinutes} دقيقة`;
      } else {
        const differenceInHours = Math.floor(differenceInMinutes / 60);
        const differenceInDays = Math.floor(differenceInHours / 24);
        const differenceInMonths = Math.floor(differenceInDays / 30);
        const differenceInYears = Math.floor(differenceInDays / 365);
        if (differenceInHours < 24) {
          if (differenceInHours === 1) timeText = 'قبل ساعة';
          else if (differenceInHours === 2) timeText = 'قبل ساعتين';
          else if (differenceInHours <= 10) timeText = `قبل ${differenceInHours} ساعات`;
          else timeText = `قبل ${differenceInHours} ساعة`;
        } else if (differenceInDays < 31) {
          if (differenceInDays === 1) timeText = 'قبل يوم';
          else if (differenceInDays === 2) timeText = 'قبل يومين';
          else if (differenceInDays <= 10) timeText = `قبل ${differenceInDays} أيام`;
          else timeText = `قبل ${differenceInDays} يوم`;
        } else if (differenceInMonths < 12) {
          if (differenceInMonths === 1) timeText = 'قبل شهر';
          else if (differenceInMonths === 2) timeText = 'قبل شهرين';
          else if (differenceInMonths <= 10) timeText = `قبل ${differenceInMonths} شهور`;
          else timeText = `قبل ${differenceInMonths} شهر`;
        } else {
          if (differenceInYears === 1) timeText = 'مضى سنة';
          else if (differenceInYears === 2) timeText = 'مضى سنتين';
          else if (differenceInYears <= 10) timeText = `مضى ${differenceInYears} سنوات`;
          else timeText = 'مضى أكثر من 10 سنوات';
        }
      }
      if (isModified) timeText = `معدلة: ${timeText}`;
      return timeText;
    } catch {
      return '';
    }
  }

  const [, forceTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceTick((x) => x + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const CountUp = ({ value = 0, duration = 800 }) => {
    const [n, setN] = useState(0);
    const startRef = useRef(0);

    useEffect(() => {
      const from = startRef.current;
      const to = Number(value) || 0;
      const t0 = performance.now();
      let raf = 0;

      const tick = (now) => {
        const p = Math.min((now - t0) / duration, 1);
        const cur = Math.round(from + (to - from) * p);
        setN(cur);
        if (p < 1) raf = requestAnimationFrame(tick);
        else startRef.current = to;
      };

      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    }, [value, duration]);

    return <>{n.toLocaleString('en-US')}</>;
  };

const fetchRamadanSchedulesCount = async ({ silent = false } = {}) => {
  if (!silent) setLoadingRamadanSchedulesCount(true);

  try {
    const PAGE_SIZE = 200;
    let offset = 0;
    let total = 0;

    while (true) {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(offset),
      }).toString();

      const res = await fetch(`/api/admin/ramadan/schedules?${params}`, {
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) throw new Error("failed_fetch_ramadan_schedules");

      const payload = await res.json().catch(() => ({}));
      const list = Array.isArray(payload?.schedules) ? payload.schedules : [];

      total += list.reduce(
        (sum, x) => sum + (Array.isArray(x?.schedule) && x.schedule.length > 0 ? 1 : 0),
        0
      );

      if (list.length < PAGE_SIZE) break;

      offset += PAGE_SIZE;
    }

    setRamadanSchedulesCount(total);
  } catch (e) {
    setRamadanSchedulesCount(0);
  } finally {
    if (!silent) setLoadingRamadanSchedulesCount(false);
  }
};


  const fetchUsers = async ({ silent = false } = {}) => {
    if (!silent) setLoadingUsers(true);
    try {
      const res = await fetch('/api/admin/users', {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Error fetching users');
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
      if (!silent) showBanner('ok', 'تم تحديث قائمة المستخدمين.');
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
      showBanner('err', 'تعذر جلب قائمة المستخدمين حالياً.');
    } finally {
      if (!silent) setLoadingUsers(false);
    }
  };

  const refreshAll = async () => {
    await Promise.allSettled([
      fetchUsers(),
      fetchRamadanSchedulesCount({ silent: true }),
    ]);
  };

  useEffect(() => {
    fetchUsers({ silent: true }).finally(() => setLoadingUsers(false));
    fetchRamadanSchedulesCount({ silent: true });
  }, []);

  const fetchUserReviews = async (user) => {
    if (!user?.email) {
      setUserReviews([]);
      return;
    }
    setLoadingReviews(true);
    setReviewsError('');
    try {
      const params = new URLSearchParams({
        email: user.email,
        limit: '200',
      }).toString();
      const res = await fetch(`/api/user/reviews?${params}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Error fetching user reviews');
      const data = await res.json();
      setUserReviews(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch user reviews:', err);
      setReviewsError('ما قدرنا نجلب تقييمات هذا المستخدم حالياً.');
      setUserReviews([]);
    } finally {
      setLoadingReviews(false);
    }
  };

  const openUserReviews = (user) => {
    setSelectedUser(user);
    setShowReviewsModal(true);
    fetchUserReviews(user);
  };

  const closeReviewsModal = () => {
    setShowReviewsModal(false);
    setSelectedUser(null);
    setUserReviews([]);
    setReviewsError('');
    setReviewToDelete(null);
  };

  const requestDeleteReview = (review) => setReviewToDelete(review);

  const cancelDeleteReview = () => {
    if (deletingReview) return;
    setReviewToDelete(null);
  };

  const confirmDeleteReview = async () => {
    if (!reviewToDelete || !selectedUser?.email) return;
    setDeletingReview(true);
    try {
      const url = `/api/reviews/${encodeURIComponent(
        reviewToDelete.id
      )}?email=${encodeURIComponent(selectedUser.email)}`;
      const res = await fetch(url, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('failed to delete review');
      const payload = await res.json().catch(() => ({}));
      if (payload && payload.ok) {
        setUserReviews((prev) => prev.filter((r) => r.id !== reviewToDelete.id));
        setUsers((prev) =>
          prev.map((u) => {
            if (u.email !== selectedUser.email) return u;
            const ratingsDelta = 1;
            const commentsDelta = reviewToDelete.comment ? 1 : 0;
            return {
              ...u,
              ratings: Math.max(0, (u.ratings || 0) - ratingsDelta),
              comments: Math.max(0, (u.comments || 0) - commentsDelta),
            };
          })
        );
        showBanner('ok', 'تم حذف التقييم بنجاح.');
      } else {
        showBanner('err', 'فشل حذف التقييم.');
      }
    } catch (err) {
      console.error('Failed to delete review:', err);
      showBanner('err', 'تعذر حذف التقييم حالياً.');
    } finally {
      setDeletingReview(false);
      setReviewToDelete(null);
    }
  };

  const confirmDeleteAllReviews = async () => {
    if (!selectedUser?.email) return;
    setDeletingAllReviews(true);
    try {
      const url = `/api/user/reviews?email=${encodeURIComponent(selectedUser.email)}`;
      const res = await fetch(url, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) throw new Error('failed to delete all reviews');

      const payload = await res.json().catch(() => ({}));
      if (payload && payload.ok) {
        const removedCount = Array.isArray(userReviews) ? userReviews.length : 0;
        const removedComments = Array.isArray(userReviews)
          ? userReviews.filter((r) => r && r.comment && String(r.comment).trim()).length
          : 0;

        setUserReviews([]);
        setUsers((prev) =>
          prev.map((u) => {
            if (u.email !== selectedUser.email) return u;
            return {
              ...u,
              ratings: Math.max(0, (u.ratings || 0) - removedCount),
              comments: Math.max(0, (u.comments || 0) - removedComments),
            };
          })
        );
        showBanner('ok', 'تم حذف جميع تقييمات المستخدم.');
      } else {
        showBanner('err', 'فشل حذف جميع التقييمات.');
      }
    } catch (err) {
      console.error('Failed to delete all reviews:', err);
      showBanner('err', 'تعذر حذف جميع التقييمات حالياً.');
    } finally {
      setDeletingAllReviews(false);
    }
  };

  const openReviewPage = (review) => {
    if (!review) return;

    const kind = resolveKind(review);
    const base = kind === 'course' ? '/course' : '/doctor-reviews';

    const rawSlug =
      review.doctorSlug ||
      review.courseSlug ||
      review.slug ||
      review.title ||
      review.course ||
      review.doctorName ||
      review.doctor ||
      review.lecturer;

    const slug = makeSlug(rawSlug);
    if (!slug) return;

    const url = `${base}/${slug}${review.id ? `?review=${encodeURIComponent(review.id)}` : ''}`;
    navigate(url);
  };

  const requestDeleteUser = (user) => setUserToDelete(user);

  const cancelDeleteUser = () => {
    if (deletingUser) return;
    setUserToDelete(null);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    setDeletingUser(true);

    const id = userToDelete.id;
    const email = userToDelete.email;

    const tryDelete = async (url) => {
      const res = await fetch(url, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) return { ok: false };
      const payload = await res.json().catch(() => ({}));
      return payload && (payload.ok || payload.success) ? { ok: true } : { ok: false };
    };

    try {
      let done = false;

      if (id !== undefined && id !== null) {
        const r1 = await tryDelete(`/api/admin/users/${encodeURIComponent(String(id))}`);
        if (r1.ok) done = true;
      }

      if (!done && email) {
        const r2 = await tryDelete(`/api/admin/users?email=${encodeURIComponent(email)}`);
        if (r2.ok) done = true;
      }

      if (done) {
        setUsers((prev) => prev.filter((u) => u.email !== email));
        showBanner('ok', 'تم حذف المستخدم.');
      } else {
        showBanner('err', 'فشل حذف المستخدم (تحقق من مسار الـ API).');
      }
    } catch (err) {
      console.error('Failed to delete user:', err);
      showBanner('err', 'تعذر حذف المستخدم حالياً.');
    } finally {
      setDeletingUser(false);
      setUserToDelete(null);
    }
  };

  const copyText = async (txt) => {
    const text = String(txt || '').trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      showBanner('ok', 'تم النسخ.');
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        showBanner('ok', 'تم النسخ.');
      } catch {
        showBanner('err', 'تعذر النسخ.');
      }
    }
  };

  const exportCSV = () => {
    const rows = (Array.isArray(users) ? users : []).map((u) => ({
      id: u.id ?? '',
      username: u.username ?? '',
      email: u.email ?? '',
      ratings: u.ratings ?? 0,
      comments: u.comments ?? 0,
      registeredSince: u.registeredSince ?? '',
    }));

    const header = ['id', 'username', 'email', 'ratings', 'comments', 'registeredSince'];
    const escape = (v) => {
      const s = String(v ?? '');
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const csv = [header.join(',')]
      .concat(rows.map((r) => header.map((k) => escape(r[k])).join(',')))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showBanner('ok', 'تم تصدير CSV.');
  };


  const fetchUserRamadanSchedule = async (user) => {
    if (!user?.email) return;
    setRamadanLoading(true);
    setRamadanError('');
    setRamadanData({ schedule: [], updatedAt: null });

    try {
      const params = new URLSearchParams({ email: user.email }).toString();

      const res = await fetch(`/api/admin/ramadan/schedule?${params}`, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) throw new Error('failed_fetch_ramadan');
      const payload = await res.json().catch(() => ({}));
      if (!payload?.ok) throw new Error(payload?.error || 'failed_fetch_ramadan');

      setRamadanData({
        schedule: Array.isArray(payload?.schedule) ? payload.schedule : [],
        updatedAt: payload?.updatedAt || null,
      });
    } catch (e) {
      console.error('Failed to fetch ramadan schedule:', e);
      setRamadanError('ما قدرنا نجلب جدول رمضان لهذا المستخدم حالياً.');
      setRamadanData({ schedule: [], updatedAt: null });
    } finally {
      setRamadanLoading(false);
    }
  };

  const openUserRamadan = (user) => {
    setSelectedUser(user);
    setShowRamadanModal(true);
    fetchUserRamadanSchedule(user);
  };

  const closeRamadanModal = () => {
    setShowRamadanModal(false);
    setRamadanError('');
    setRamadanData({ schedule: [], updatedAt: null });
    setConfirmDeleteScheduleOpen(false);
    setDeletingSchedule(false);


    if (!showReviewsModal) setSelectedUser(null);
  };

  const requestDeleteSchedule = () => {
    if (!selectedUser?.email) return;
    setConfirmDeleteScheduleOpen(true);
  };

  const cancelDeleteSchedule = () => {
    if (deletingSchedule) return;
    setConfirmDeleteScheduleOpen(false);
  };

  const confirmDeleteSchedule = async () => {
    if (!selectedUser?.email) return;
    setDeletingSchedule(true);
    try {
      const params = new URLSearchParams({ email: selectedUser.email }).toString();
      const res = await fetch(`/api/admin/ramadan/schedule?${params}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) throw new Error('failed_delete_schedule');
      const payload = await res.json().catch(() => ({}));
      if (!payload?.ok) throw new Error(payload?.error || 'failed_delete_schedule');

      setRamadanData({ schedule: [], updatedAt: null });
      setRamadanSchedulesCount((x) => Math.max(0, (Number(x) || 0) - 1));
      showBanner('ok', 'تم حذف جدول رمضان لهذا المستخدم.');
      setConfirmDeleteScheduleOpen(false);
    } catch (e) {
      console.error('Failed to delete schedule:', e);
      showBanner('err', 'تعذر حذف جدول رمضان حالياً.');
    } finally {
      setDeletingSchedule(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowSuggestions(value.trim().length > 0);
  };

  const handleSuggestionPick = (user, mode = 'username') => {
    const v = mode === 'email' ? user.email : user.username;
    setSearchTerm(v || '');
    setShowSuggestions(false);
    setTimeout(() => inputRef.current?.focus?.(), 0);
  };

  const filteredUsers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const list = Array.isArray(users) ? users : [];
    if (!q) return list;

    return list.filter((user) => {
      const u = (user.username || '').toLowerCase();
      const e = (user.email || '').toLowerCase();
      const id = String(user.id ?? '').toLowerCase();
      return u.includes(q) || e.includes(q) || id.includes(q);
    });
  }, [users, searchTerm]);

  useEffect(() => {
    const onDocMouseDown = (ev) => {
      const a = suggestionsRef.current;
      const b = inputRef.current;
      if (!a || !b) return;
      if (a.contains(ev.target) || b.contains(ev.target)) return;
      setShowSuggestions(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  useEffect(() => {
    const onKey = (ev) => {
      if (ev.key === 'Escape') {
        setShowSuggestions(false);
        setSearchFocused(false);
        if (searchTerm) setSearchTerm('');
      }
      if (ev.key === 'Enter' && showSuggestions) {
        const first = filteredUsers[0];
        if (first) {
          handleSuggestionPick(first, 'username');
          setShowSuggestions(false);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showSuggestions, filteredUsers, searchTerm]);

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const baseList = useMemo(() => {
    const list = Array.isArray(filteredUsers) ? filteredUsers : [];
    if (activityFilter === 'hasAny') return list.filter((u) => (u.ratings || 0) > 0 || (u.comments || 0) > 0);
    if (activityFilter === 'hasReviews') return list.filter((u) => (u.ratings || 0) > 0);
    if (activityFilter === 'hasComments') return list.filter((u) => (u.comments || 0) > 0);
    return list;
  }, [filteredUsers, activityFilter]);

  const sortedUsers = useMemo(() => {
    const arr = [...baseList];
    const dir = sortOrder === 'desc' ? -1 : 1;

    const cmp = (a, b) => {
      if (sortKey === 'username') {
        const aa = String(a.username || '').toLowerCase();
        const bb = String(b.username || '').toLowerCase();
        if (aa < bb) return -1 * dir;
        if (aa > bb) return 1 * dir;
        return 0;
      }

      if (sortKey === 'ratings') {
        const aa = Number(a.ratings || 0);
        const bb = Number(b.ratings || 0);
        return (aa - bb) * dir;
      }

      if (sortKey === 'comments') {
        const aa = Number(a.comments || 0);
        const bb = Number(b.comments || 0);
        return (aa - bb) * dir;
      }

      const da = toValidDate(a.registeredSince) || new Date(0);
      const db = toValidDate(b.registeredSince) || new Date(0);
      return (da - db) * dir;
    };

    arr.sort(cmp);
    return arr;
  }, [baseList, sortKey, sortOrder]);

  const totalUsers = Array.isArray(users) ? users.length : 0;
  const totalRatings = Array.isArray(users) ? users.reduce((sum, u) => sum + (u.ratings || 0), 0) : 0;
  const totalComments = Array.isArray(users) ? users.reduce((sum, u) => sum + (u.comments || 0), 0) : 0;

  const lastRegistered = useMemo(() => {
    const d =
      (Array.isArray(users) ? users : [])
        .map((u) => toValidDate(u.registeredSince))
        .filter(Boolean)
        .sort((a, b) => b - a)[0] || null;
    return d;
  }, [users]);

  const lastRegisteredText = lastRegistered ? timeAgoClient(lastRegistered) : 'لا يوجد بيانات كافية';

  const metricCardBase = {
    background: 'radial-gradient(55% 90% at 50% -5%,rgba(40,227,218,.18) 0%,rgba(40,227,218,.04) 55%,rgba(8,20,35,.9) 100%)',
    boxShadow: '0 4px 20px rgba(0,0,0,.3)',
    borderRadius: '18px',
    border: '1px solid rgba(40,227,218,.22)',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'transform .2s, box-shadow .2s',
    cursor: 'default',
    backdropFilter: 'blur(6px)',
  };

  const onMetricHoverIn = (e) => {
    e.currentTarget.style.transform = 'translateY(-4px)';
    e.currentTarget.style.boxShadow = '0 20px 60px rgba(0,0,0,.55)';
  };

  const onMetricHoverOut = (e) => {
    e.currentTarget.style.transform = 'translateY(0px)';
    e.currentTarget.style.boxShadow = '0 10px 40px rgba(0,0,0,.35)';
  };

  const badge = (text, tone = 'aqua') => {
    const map = {
      aqua: 'bg-[#0B2A30] text-[#D0F7FF] border-[#28E3DA55]',
      gray: 'bg-[#0B1723] text-[#C4D6E5] border-[#2C4258]',
      warn: 'bg-[#2B1014] text-[#FFECEC] border-[#F76A7A99]',
    };
    const cls = map[tone] || map.gray;
    return (
      <span className={`px-2 py-1 rounded-full text-[11px] border ${cls}`}>
        {text}
      </span>
    );
  };

  const topBarBtn = (active) => ({
    color: active ? '#081423' : '#D0E3F0',
    background: active ? 'linear-gradient(135deg,#28E3DA,#7DF9E8)' : 'transparent',
    border: active ? '1px solid rgba(8,20,35,.6)' : '1px solid transparent',
    boxShadow: active ? '0 0 18px rgba(40,227,218,.55)' : 'none',
  });

  const resultSummaryText = useMemo(() => {
    const q = searchTerm.trim();
    const base = q ? `نتائج البحث: ${sortedUsers.length} من ${totalUsers}` : `المستخدمون: ${sortedUsers.length}`;
    return base;
  }, [searchTerm, sortedUsers.length, totalUsers]);

  const navItemStyle = {
    height: '24px',
    fontFamily: 'Alexandria, Roboto',
    fontWeight: 800,
    fontSize: '15px',
    lineHeight: '24px',
    letterSpacing: '0.3px',
    color: '#B8C5CE',
    textShadow: '0 0 10px rgba(177,223,255,.25)',
  };

  return (
    <div
      className="min-h-screen text-white bg-transparent"
      style={{
        direction: 'rtl',
        display: 'flex',
        flexDirection: 'row',
        background:
          'linear-gradient(0deg, rgba(255,255,255,0.02), rgba(255,255,255,0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40,227,218,0.16) 0%, rgba(40,227,218,0) 100%), #081423',
      }}
    >
      <div className="hidden md:block" style={{ position: 'sticky', top: 0, height: '100vh', zIndex: 200, flexShrink: 0 }}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      </div>

      <div className="md:hidden">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      </div>

      <div className="flex-1 flex flex-col md:ml-0" style={{ minWidth: 0 }}>
        <div
          className="flex-1"
          style={{
            padding: isMobile ? '16px 12px 80px' : isTablet ? '20px 24px 24px' : '24px 48px 32px',
          }}
        >
          <nav style={{
            display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'flex-start' : 'space-evenly',
            flexWrap: 'wrap', gap: isMobile ? '6px' : '4px',
            padding: isMobile ? '10px 12px' : '0 16px',
            height: isMobile ? 'auto' : '52px',
            background: 'radial-gradient(43.64% 77.08% at 50% -10.42%,rgba(40,227,218,.2) 0%,rgba(40,227,218,0) 100%),rgba(9,28,43,.6)',
            border: '1px solid rgba(40,227,218,.2)',
            borderRadius: '16px',
            backdropFilter: 'blur(4px)',
            marginBottom: '24px',
          }}>
            {[
              { label: 'نظرة عامة',           path: '/dashboard' },
              { label: 'إدارة المستخدمين',    active: true },
              { label: 'سجل النشاط',          path: '/activity-log' },
              { label: 'الإبلاغات',           path: '/reports' },
              { label: 'إضافة مقرر / محاضر', path: '/add-course-lecturer' },
              { label: 'إدارة المقررات',      path: '/manage-courses-lecturers' },
            ].map((item, i) => (
              <React.Fragment key={item.label}>
                {i > 0 && !isMobile && <span style={{ color: 'rgba(255,255,255,.15)', fontSize: '18px', userSelect: 'none' }}>|</span>}
                <span
                  onClick={() => item.path && navigate(item.path)}
                  style={{
                    fontFamily: 'Alexandria,sans-serif', fontSize: isMobile ? '12px' : '14px',
                    fontWeight: item.active ? 800 : 500, color: item.active ? '#7DF9E8' : '#94a3b8',
                    cursor: item.path ? 'pointer' : 'default',
                    padding: isMobile ? '5px 10px' : '0 4px', borderRadius: isMobile ? '8px' : 0,
                    background: item.active && isMobile ? 'rgba(40,227,218,.1)' : 'transparent',
                    transition: 'color .15s', whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={e => { if (item.path) e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { if (item.path) e.currentTarget.style.color = '#94a3b8'; }}
                >{item.label}</span>
              </React.Fragment>
            ))}
          </nav>

          {banner.text && (
            <div className="flex justify-center mb-6 px-4" dir="rtl">
              <div
                className="w-full max-w-[1108px] rounded-[18px] px-4 py-3 border"
                style={{
                  borderColor: banner.type === 'ok' ? 'rgba(40,227,218,.35)' : 'rgba(246,65,79,.35)',
                  background:
                    banner.type === 'ok'
                      ? 'radial-gradient(60% 140% at 50% -20%, rgba(40,227,218,.22) 0%, rgba(40,227,218,0) 45%), rgba(6,18,32,.92)'
                      : 'radial-gradient(60% 140% at 50% -20%, rgba(246,65,79,.18) 0%, rgba(246,65,79,0) 45%), rgba(6,18,32,.92)',
                  boxShadow: '0 12px 40px rgba(0,0,0,.40)',
                }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="text-[13px] font-bold" style={{ color: banner.type === 'ok' ? '#D0F7FF' : '#FFECEC' }}>
                    {banner.text}
                  </div>
                  <button
                    onClick={() => setBanner({ type: '', text: '' })}
                    className="text-[#B8C5CE] hover:text-white text-[12px]"
                  >
                    إغلاق
                  </button>
                </div>
              </div>
            </div>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(1,minmax(0,1fr))' : 'repeat(4,minmax(0,1fr))',
              gap: isMobile ? '12px' : '20px',
              width: '100%',
              maxWidth: '1108px',
              margin: '0 auto 18px',
            }}
          >
            <div style={metricCardBase} onMouseEnter={onMetricHoverIn} onMouseLeave={onMetricHoverOut}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ fontFamily: 'Alexandria', fontSize: '14px', color: '#E8F5FF' }}>إجمالي المستخدمين</span>
                <div style={{ width: 32, height: 32, borderRadius: '999px', background: 'rgba(8,20,35,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 18 }}>👥</span>
                </div>
              </div>

              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'Alexandria', fontWeight: 800, fontSize: '22px' }}>
                  <CountUp value={totalUsers} />
                </span>
                <span style={{ fontFamily: 'Alexandria', fontSize: '12px', color: '#B8C5CE' }}>
                  {lastRegisteredText === 'لا يوجد بيانات كافية' ? 'لا يوجد تاريخ تسجيل واضح' : `آخر تسجيل: ${lastRegisteredText}`}
                </span>
              </div>
            </div>

            <div style={metricCardBase} onMouseEnter={onMetricHoverIn} onMouseLeave={onMetricHoverOut}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ fontFamily: 'Alexandria', fontSize: '14px', color: '#E8F5FF' }}>مجموع التقييمات</span>
                <div style={{ width: 32, height: 32, borderRadius: '999px', background: 'rgba(8,20,35,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 18 }}>⭐</span>
                </div>
              </div>

              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'Alexandria', fontWeight: 800, fontSize: '22px' }}>
                  <CountUp value={totalRatings} />
                </span>
                <span style={{ fontFamily: 'Alexandria', fontSize: '12px', color: '#B8C5CE' }}>
                  متوسط لكل مستخدم: {totalUsers > 0 ? (totalRatings / totalUsers).toFixed(1) : '0.0'}
                </span>
              </div>
            </div>

            <div style={metricCardBase} onMouseEnter={onMetricHoverIn} onMouseLeave={onMetricHoverOut}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ fontFamily: 'Alexandria', fontSize: '14px', color: '#E8F5FF' }}>مجموع التعليقات</span>
                <div style={{ width: 32, height: 32, borderRadius: '999px', background: 'rgba(8,20,35,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 18 }}>💬</span>
                </div>
              </div>

              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'Alexandria', fontWeight: 800, fontSize: '22px' }}>
                  <CountUp value={totalComments} />
                </span>
                <span style={{ fontFamily: 'Alexandria', fontSize: '12px', color: '#B8C5CE' }}>تفاعل المستخدمين مع التقييمات</span>
              </div>
            </div>

            <div style={metricCardBase} onMouseEnter={onMetricHoverIn} onMouseLeave={onMetricHoverOut}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ fontFamily: 'Alexandria', fontSize: '14px', color: '#E8F5FF' }}>جداول رمضان المحفوظة</span>
                <div style={{ width: 32, height: 32, borderRadius: '999px', background: 'rgba(8,20,35,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 18 }}>🗓️</span>
                </div>
              </div>

              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'Alexandria', fontWeight: 800, fontSize: '22px' }}>
                  <CountUp value={ramadanSchedulesCount} />
                </span>
                <span style={{ fontFamily: 'Alexandria', fontSize: '12px', color: '#B8C5CE' }}>
                  {loadingRamadanSchedulesCount ? 'تحديث…' : 'مستخدمين حفظوا جدولهم'}
                </span>
              </div>
            </div>

          </div>

          <div className="flex justify-center mb-4 px-4" dir="rtl">
            <div
              className="w-full max-w-[1108px] px-3 py-3"
              style={{
                background: 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.14) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.9)',
                border: '1px solid rgba(40,227,218,.22)',
                borderRadius: '18px',
                boxShadow: '0 8px 32px rgba(0,0,0,.35)',
                backdropFilter: 'blur(6px)',
              }}
            >
              <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
                <div className="text-[12px] text-[#9EB4C8]">
                  <span className="text-white font-bold">{resultSummaryText}</span>
                  <span className="mx-2 opacity-60">•</span>
                  <span className="opacity-90">الفرز والفلترة هنا عشان ما تصير الصفحة “حوسه”</span>
                </div>

                <div className="flex flex-wrap gap-2 justify-end items-center">
                  <button
                    onClick={() => refreshAll()}
                    disabled={loadingUsers}
                    className="px-3 py-2 rounded-[14px] text-[12px] font-bold border border-[#28E3DA33] hover:bg-[#111D2A] disabled:opacity-60"
                    style={{ color: '#D0E3F0' }}
                    title="تحديث"
                  >
                    {loadingUsers ? 'جاري…' : 'تحديث'}
                  </button>

                  <button
                    onClick={exportCSV}
                    className="px-3 py-2 rounded-[14px] text-[12px] font-bold border border-[#28E3DA33] hover:bg-[#111D2A]"
                    style={{ color: '#D0E3F0' }}
                    title="تصدير CSV"
                  >
                    تصدير CSV
                  </button>

                  <button
                    onClick={() => setShowSendNotifModal(true)}
                    className="px-3 py-2 rounded-[14px] text-[12px] font-bold border border-[#28E3DA55] hover:bg-[#0d2035]"
                    style={{ color: '#28E3DA' }}
                    title="إرسال إشعار للمستخدمين"
                  >
                    إرسال إشعار
                  </button>

                  <div className="flex items-center gap-2 rounded-[14px] px-2 py-2 border border-[rgba(40,227,218,.22)] bg-[rgba(8,20,35,.8)]">
                    <span className="text-[12px] text-[#9EB4C8]">فرز</span>
                    <select
                      value={sortKey}
                      onChange={(e) => setSortKey(e.target.value)}
                      className="bg-transparent outline-none text-[12px] text-white"
                      style={{ direction: 'rtl' }}
                    >
                      <option value="registeredSince" className="bg-[#081423]">تاريخ التسجيل</option>
                      <option value="username" className="bg-[#081423]">اسم المستخدم</option>
                      <option value="ratings" className="bg-[#081423]">عدد التقييمات</option>
                      <option value="comments" className="bg-[#081423]">عدد التعليقات</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-2 rounded-[18px] px-2 py-2 border border-[rgba(40,227,218,.22)] bg-[rgba(8,20,35,.8)]">
                    <button
                      onClick={() => setSortOrder('desc')}
                      className="px-3 py-2 rounded-[14px] font-bold flex items-center gap-2 transition-all"
                      style={topBarBtn(sortOrder === 'desc')}
                    >
                      <span style={{ fontSize: 16 }}>⬆️</span>
                      <span className="text-[12px]">الأعلى</span>
                    </button>
                    <button
                      onClick={() => setSortOrder('asc')}
                      className="px-3 py-2 rounded-[14px] font-bold flex items-center gap-2 transition-all"
                      style={topBarBtn(sortOrder === 'asc')}
                    >
                      <span style={{ fontSize: 16 }}>⬇️</span>
                      <span className="text-[12px]">الأقل</span>
                    </button>
                  </div>

                  <div className="flex items-center gap-2 rounded-[14px] px-2 py-2 border border-[rgba(40,227,218,.22)] bg-[rgba(8,20,35,.8)]">
                    <span className="text-[12px] text-[#9EB4C8]">فلتر</span>
                    <select
                      value={activityFilter}
                      onChange={(e) => setActivityFilter(e.target.value)}
                      className="bg-transparent outline-none text-[12px] text-white"
                      style={{ direction: 'rtl' }}
                    >
                      <option value="all" className="bg-[#081423]">الكل</option>
                      <option value="hasAny" className="bg-[#081423]">نشاط (أي)</option>
                      <option value="hasReviews" className="bg-[#081423]">لديه تقييمات</option>
                      <option value="hasComments" className="bg-[#081423]">لديه تعليقات</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {            }
          <div className="flex flex-col items-center w-full max-w-4xl mx-auto px-4 mb-8" dir="rtl">
            <div className="w-full max-w-2xl mb-4" style={{ display: 'flex', justifyContent: 'center' }}>
              <div
                className="flex flex-col items-start gap-2 w-full md:w-[506px] h-[54px] rounded-[12px] transition-all"
                style={{
                  boxSizing: 'border-box',
                  border: searchFocused ? '1px solid rgba(40,227,218,.85)' : '1px solid rgba(40,227,218,.22)',
                  background: 'rgba(8,20,35,.8)',
                  boxShadow: searchFocused ? '0 0 20px rgba(40,227,218,.4)' : '0 4px 16px rgba(0,0,0,.3)',
                  backdropFilter: 'blur(4px)',
                  padding: '3px 5px',
                }}
              >
                <div className="relative flex flex-row items-center gap-[17px] w-full md:w-[496px] h-[48px]">
                  <div className="flex flex-row items-center p-0 flex-1 md:w-[432px] h-[48px]">
                    <div className="flex items-center justify-center w-[48px] h-[48px] flex-shrink-0">
                      <svg width="35" height="35" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M39.2 42L26.6 29.4C25.6 30.2 24.45 30.8333 23.15 31.3C21.85 31.7667 20.4667 32 19 32C15.3667 32 12.292 30.7413 9.776 28.224C7.26 25.7067 6.00133 22.632 6 19C5.99867 15.368 7.25733 12.2933 9.776 9.776C12.2947 7.25867 15.3693 6 19 6C22.6307 6 25.706 7.25867 28.226 9.776C30.746 12.2933 32.004 15.368 32 19C32 20.4667 31.7667 21.85 31.3 23.15C30.8333 24.45 30.2 25.6 29.4 26.6L42 39.2L39.2 42ZM19 28C21.5 28 23.6253 27.1253 25.376 25.376C27.1267 23.6267 28.0013 21.5013 28 19C27.9987 16.4987 27.124 14.374 25.376 12.626C23.628 10.878 21.5027 10.0027 19 10C16.4973 9.99733 14.3727 10.8727 12.626 12.626C10.8793 14.3793 10.004 16.504 10 19C9.996 21.496 10.8713 23.6213 12.626 25.376C14.3807 27.1307 16.5053 28.0053 19 28Z"
                          fill="#EEEEEE"
                        />
                      </svg>
                    </div>

                    <input
                      ref={inputRef}
                      type="text"
                      placeholder="ابحث بالاسم / البريد / رقم المستخدم…"
                      className="flex-1 bg-transparent outline-none min-w-0 text-end placeholder:text-end"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onFocus={() => {
                        setSearchFocused(true);
                        setShowSuggestions(searchTerm.trim().length > 0);
                      }}
                      onBlur={() => {
                        setSearchFocused(false);
                        setTimeout(() => setShowSuggestions(false), 180);
                      }}
                      style={{
                        height: '14px',
                        fontFamily: 'Alexandria',
                        fontWeight: 800,
                        fontSize: '14px',
                        lineHeight: '100%',
                        color: '#EEEEEE',
                        direction: 'rtl',
                      }}
                    />

                    {searchTerm && (
                      <button
                        onClick={() => {
                          setSearchTerm('');
                          setShowSuggestions(false);
                          inputRef.current?.focus?.();
                        }}
                        className="w-[44px] h-[48px] flex items-center justify-center text-[#B8C5CE] hover:text-white"
                        title="مسح"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {showSuggestions && filteredUsers.length > 0 && (
              <div className="w-full max-w-2xl mb-4 relative" ref={suggestionsRef}>
                <div
                  className="absolute top-0 left-0 right-0 z-50 rounded-[12px] shadow-lg max-h-60 overflow-y-auto [&::-webkit-scrollbar]:hidden text-end"
                  style={{
                    border: '1px solid rgba(40,227,218,.22)',
                    background: 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.12) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.97)',
                    direction: 'rtl',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    backdropFilter: 'blur(6px)',
                  }}
                >
                  {filteredUsers.slice(0, 8).map((user) => (
                    <div
                      key={`${user.id}-${user.email}-s`}
                      className="px-4 py-3 border-b last:border-b-0 transition-colors"
                      style={{ borderColor: 'rgba(40,227,218,.12)' }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-white text-[14px] font-bold">{user.username}</div>
                        <div className="flex gap-2">
                          <button
                            className="px-3 py-1.5 rounded-[12px] text-[12px] font-bold text-[#081423] bg-gradient-to-tr from-[#28E3DA] to-[#7DF9E8] hover:brightness-110"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSuggestionPick(user, 'username')}
                          >
                            اختيار الاسم
                          </button>
                          <button
                            className="px-3 py-1.5 rounded-[12px] text-[12px] font-bold text-[#D0E3F0] border border-[#2C4258] hover:bg-[#111D2A]"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSuggestionPick(user, 'email')}
                          >
                            اختيار البريد
                          </button>
                        </div>
                      </div>
                      <div className="mt-1 text-[#B8C5CE] text-[12px] ltr">{user.email}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {                }
            <div className="w-full space-y-4" dir="rtl">
              {loadingUsers && (
                <div className="text-center text-[#B8C5CE] py-10">
                  جاري التحميل…
                </div>
              )}

              {!loadingUsers && sortedUsers.length === 0 && (
                <div className="text-center text-[#B8C5CE] py-10">
                  لا توجد نتائج.
                </div>
              )}

              {!loadingUsers &&
                sortedUsers.map((user) => (
                  <div
                    key={`${user.id}-${user.email}`}
                    className="p-6 transition-all hover:-translate-y-1"
                    style={{
                      background: 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.14) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.9)',
                      border: '1px solid rgba(40,227,218,.22)',
                      borderRadius: '20px',
                      boxShadow: '0 8px 32px rgba(0,0,0,.35)',
                      backdropFilter: 'blur(6px)',
                    }}
                  >
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <div className="flex flex-wrap items-center gap-2">
                        {Number(user.ratings || 0) > 0 ? badge(`تقييمات: ${user.ratings}`, 'aqua') : badge('بدون تقييمات', 'gray')}
                        {Number(user.comments || 0) > 0 ? badge(`تعليقات: ${user.comments}`, 'aqua') : badge('بدون تعليقات', 'gray')}
                        {user.registeredSince ? badge(timeAgoClient(user.registeredSince), 'gray') : badge('تاريخ غير واضح', 'warn')}
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => requestDeleteUser(user)}
                          className="w-9 h-9 rounded-full flex items-center justify-center border border-[#F76A7A66] bg-[#2B1014] hover:bg-[#3A151A] text-[#FFECEC]"
                          title="حذف المستخدم"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M7 21C6.45 21 5.979 20.804 5.586 20.411C5.193 20.018 4.997 19.547 4.998 18.999V7H4C3.717 7 3.479 6.904 3.286 6.712C3.093 6.52 2.998 6.283 3 6C3.002 5.717 3.098 5.479 3.289 5.286C3.48 5.093 3.717 4.998 4 5H9C9 4.717 9.096 4.479 9.288 4.286C9.48 4.093 9.717 3.998 10 4H14C14.283 4 14.521 4.096 14.714 4.288C14.907 4.48 15.002 4.717 15 5H20C20.283 5 20.521 5.096 20.714 5.288C20.907 5.48 21.002 5.717 21 6C20.998 6.283 20.902 6.521 20.711 6.714C20.52 6.907 20.283 7.002 20 7V19C20 19.55 19.804 20.021 19.411 20.414C19.018 20.807 18.547 21.003 17.999 21H7Z"
                              fill="#FFECEC"
                            />
                          </svg>
                        </button>

                        <button
                          onClick={() => copyText(user.email)}
                          className="w-9 h-9 rounded-full flex items-center justify-center border border-[#2C4258] bg-[#0B1723] hover:bg-[#122133] text-[#C4D6E5]"
                          title="نسخ البريد"
                        >
                          ⧉
                        </button>

                        <button
                          onClick={() => openUserReviews(user)}
                          className="w-9 h-9 rounded-full flex items-center justify-center border border-[#2C4258] bg-[#0B1723] hover:bg-[#122133] text-[#C4D6E5]"
                          title="عرض تقييمات المستخدم"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M8 9H16M8 13H14M18 4C18.7956 4 19.5587 4.31607 20.1213 4.87868C20.6839 5.44129 21 6.20435 21 7V15C21 15.7956 20.6839 16.5587 20.1213 17.1213C19.5587 17.6839 18.7956 18 18 18H13L8 21V18H6C5.20435 18 4.44129 17.6839 3.87868 17.1213C3.31607 16.5587 3 15.7956 3 15V7C3 6.20435 3.31607 5.44129 3.87868 4.87868C4.44129 4.31607 5.20435 4 6 4H18Z"
                              stroke="#C5E6FD"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>

                        {                           }
                        <button
                          onClick={() => openUserRamadan(user)}
                          className="w-9 h-9 rounded-full flex items-center justify-center border border-[#28E3DA55] bg-[#0B2A30] hover:bg-[#10343B] text-[#D0F7FF]"
                          title="عرض جدول رمضان"
                        >
                          🗓️
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3 text-end">
                      <div className="flex justify-between items-center">
                        <div className="text-white text-[14px] font-bold">{user.username}</div>
                        <div className="text-white text-[14px]">اسم المستخدم</div>
                      </div>

                      <div className="flex justify-between items-center">
                        <div className="text-[#B8C5CE] text-[14px] ltr">{user.email}</div>
                        <div className="text-[#B8C5CE] text-[14px]">البريد الإلكتروني</div>
                      </div>

                      <div className="flex justify-between items-center">
                        <div className="text-[#B8C5CE] text-[14px] ltr">{user.ratings || 0}</div>
                        <div className="text-[#B8C5CE] text-[14px]">عدد التقييمات</div>
                      </div>

                      <div className="flex justify-between items-center">
                        <div className="text-[#B8C5CE] text-[14px] ltr">{user.comments || 0}</div>
                        <div className="text-[#B8C5CE] text-[14px]">عدد التعليقات</div>
                      </div>

                      <div className="flex justify-between items-center">
                        <div className="text-[#B8C5CE] text-[14px] ltr">{timeAgoClient(user.registeredSince) || 'غير متوفر'}</div>
                        <div className="text-[#B8C5CE] text-[14px]">تاريخ التسجيل</div>
                      </div>
                    </div>

                    <div className="flex flex-wrap justify-start md:justify-end gap-2 mt-5">
                      <button
                        onClick={() => openUserReviews(user)}
                        className="px-4 py-2 rounded-[14px] text-[13px] font-semibold text-[#081423] bg-gradient-to-tr from-[#28E3DA] to-[#7DF9E8] shadow-[0_0_16px_rgba(40,227,218,.6)] hover:brightness-110"
                      >
                        عرض التقييمات لهذا المستخدم
                      </button>

                      {         }
                      <button
                        onClick={() => openUserRamadan(user)}
                        className="px-4 py-2 rounded-[14px] text-[13px] font-bold text-[#D0F7FF] border border-[#28E3DA55] bg-[#0B2A30] hover:bg-[#10343B]"
                      >
                        عرض جدول رمضان
                      </button>

                      <button
                        onClick={() => copyText(user.email)}
                        className="px-4 py-2 rounded-[14px] text-[13px] font-bold text-[#D0E3F0] border border-[#2C4258] hover:bg-[#111D2A]"
                      >
                        نسخ البريد
                      </button>

                      <button
                        onClick={() => requestDeleteUser(user)}
                        className="px-4 py-2 rounded-[14px] text-[13px] font-bold text-[#FFECEC] bg-[#2B1014] border border-[#F76A7A99] hover:bg-[#3A151A]"
                      >
                        حذف المستخدم
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {                   }
        <UserReviewsModal
          open={showReviewsModal}
          user={selectedUser}
          reviews={userReviews}
          loading={loadingReviews}
          error={reviewsError}
          onClose={closeReviewsModal}
          onDelete={requestDeleteReview}
          onOpenReviewPage={openReviewPage}
          onDeleteAll={confirmDeleteAllReviews}
          deletingAll={deletingAllReviews}
        />

        {                         }
        <UserRamadanModal
  open={showRamadanModal}
  user={selectedUser}
  data={ramadanData}
  loading={ramadanLoading}
  error={ramadanError}
  onClose={closeRamadanModal}
  onRequestDeleteSchedule={requestDeleteSchedule}
  onDeleteRow={deleteRamadanRow}
        />

        {                             }
        <ConfirmDialog
          open={!!reviewToDelete}
          title="حذف التقييم"
          message="هل أنت متأكد من حذف هذا التقييم؟ لا يمكن التراجع عن هذه العملية."
          confirmLabel={deletingReview ? 'جاري الحذف…' : 'حذف نهائي'}
          cancelLabel="إلغاء"
          onConfirm={confirmDeleteReview}
          onCancel={cancelDeleteReview}
          loading={deletingReview}
        />

        {                         }
        <ConfirmDialog
          open={!!userToDelete}
          title="حذف المستخدم"
          message="هل أنت متأكد من حذف هذا المستخدم؟ قد يؤدي ذلك إلى حذف بيانات مرتبطة به حسب إعدادات النظام."
          confirmLabel={deletingUser ? 'جاري الحذف…' : 'حذف المستخدم'}
          cancelLabel="إلغاء"
          onConfirm={confirmDeleteUser}
          onCancel={cancelDeleteUser}
          loading={deletingUser}
        />

        {                                           }
        <ConfirmDialog
          open={confirmDeleteScheduleOpen}
          title="حذف جدول رمضان"
          message="هل أنت متأكد؟ سيتم حذف جدول رمضان لهذا المستخدم نهائيًا."
          confirmLabel={deletingSchedule ? 'جاري الحذف…' : 'حذف الجدول'}
          cancelLabel="إلغاء"
          onConfirm={confirmDeleteSchedule}
          onCancel={cancelDeleteSchedule}
          loading={deletingSchedule}
        />

        {                             }
        <SendNotificationModal
          open={showSendNotifModal}
          users={users}
          onClose={() => setShowSendNotifModal(false)}
          onSuccess={(sent) => showBanner('ok', `تم إرسال الإشعار لـ ${sent} مستخدم بنجاح.`)}
        />

        <MobileBottomNav />
      </div>
    </div>
  );
};

export default UserManagementPage;
